import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'

export type VirtualMarketerPlan = StripeProduct & {
  key: PaymentPlanKey
  prices: StripePrice[]
}

export type AutopilotPlan = VirtualMarketerPlan

export interface StripeProduct {
  id: string
  object: string
  active: boolean
  attributes: string[]
  created: number
  default_price: string
  description: string | null
  features: string[]
  images: string[]
  livemode: boolean
  marketing_features: string[]
  metadata: Record<string, any>
  name: string
  package_dimensions: any | null
  shippable: boolean | null
  statement_descriptor: string | null
  tax_code: string
  type: string
  unit_label: string | null
  updated: number
  url: string | null
}

export interface StripePrice {
  id: string
  object: string
  active: boolean
  billing_scheme: string
  created: number
  currency: string
  custom_unit_amount: any | null
  livemode: boolean
  lookup_key: string | null
  metadata: {
    additional_key?: string
    percent_off?: string
    credit_limit?: string
  }
  nickname?: string
  product: string
  /**
   * `null` for one-time prices (`type: 'one_time'`). Stripe only populates this for
   * recurring prices, so never read through it unguarded — prefer filtering to
   * recurring prices when listing subscription intervals.
   */
  recurring: {
    aggregate_usage: string | null
    interval: string
    interval_count: number
    meter: string | null
    trial_period_days: number | null
    usage_type: string
  } | null
  tax_behavior: string
  tiers_mode: string | null
  transform_quantity: any | null
  type: string
  unit_amount: number
}

/** A `StripePrice` known to be a subscription price, so `recurring` is safe to read. */
export type RecurringStripePrice = StripePrice & {
  recurring: NonNullable<StripePrice['recurring']>
}

/** Excludes one-time prices (e.g. content-credit packs), which have `recurring: null`. */
export const isRecurringPrice = (price: StripePrice): price is RecurringStripePrice => !!price.recurring

/**
 * Narrows a price list to subscription prices. Use this when rendering billing intervals —
 * a one-time price has no interval and must not appear in an interval picker.
 */
export const recurringPrices = (prices: StripePrice[] = []): RecurringStripePrice[] => prices.filter(isRecurringPrice)

export interface StripeInvoice {
  id: string
  object: 'invoice'
  account_country: string
  account_name: string
  account_tax_ids: string[] | null
  amount_due: number
  amount_paid: number
  amount_overpaid: number
  amount_remaining: number
  amount_shipping: number
  application: string | null
  attempt_count: number
  attempted: boolean
  auto_advance: boolean
  automatic_tax: {
    enabled: boolean
    liability: string | null
    status: string | null
  }
  billing_reason: string
  collection_method: string
  created: number
  currency: string
  custom_fields: any[] | null
  customer: string
  customer_address: any | null
  customer_email: string
  customer_name: string
  customer_phone: string | null
  customer_shipping: any | null
  customer_tax_exempt: string
  customer_tax_ids: any[]
  default_payment_method: string | null
  default_source: string | null
  default_tax_rates: any[]
  description: string | null
  discounts: any[]
  due_date: number | null
  ending_balance: number | null
  footer: string | null
  from_invoice: any | null
  hosted_invoice_url: string | null
  invoice_pdf: string | null
  issuer: {
    type: string
  }
  last_finalization_error: any | null
  latest_revision: string | null
  lines: StripeInvoiceLines
  livemode: boolean
  metadata: Record<string, any>
  next_payment_attempt: number | null
  number: string | null
  on_behalf_of: string | null
  parent: string | null
  payment_settings: {
    default_mandate: string | null
    payment_method_options: any | null
    payment_method_types: string[] | null
  }
  period_end: number
  period_start: number
  post_payment_credit_notes_amount: number
  pre_payment_credit_notes_amount: number
  receipt_number: string | null
  shipping_cost: any | null
  shipping_details: any | null
  starting_balance: number
  statement_descriptor: string | null
  status: string
  status_transitions: {
    finalized_at: number | null
    marked_uncollectible_at: number | null
    paid_at: number | null
    voided_at: number | null
  }
  subtotal: number
  subtotal_excluding_tax: number
  test_clock: string | null
  total: number
  total_discount_amounts: any[]
  total_excluding_tax: number
  total_taxes: any[]
  webhooks_delivered_at: number
}

export interface StripeInvoiceLines {
  object: 'list'
  data: any[]
  has_more: boolean
  total_count: number
  url: string
}
